<template>
  <base-draggable-modal
      :name="modal_name"
      max-height="80vh"
      width="50vw"
      :fullscreen="$vuetify.display.xsOnly"
      :ref="modal_name">
    <v-card rounded="xl">
      <base-modal>
        <template #header>
          <v-toolbar class="draggable_selector" flat color="secondary" density="compact">
            <v-toolbar-title v-if="label" class="text-primary">{{ label }}</v-toolbar-title>
            <v-toolbar-title v-else class="text-primary">{{ $store.getters.translate('create') }} {{ $store.getters.translate(page.model) }}</v-toolbar-title>
          </v-toolbar>
        </template>
        <template #content>
            <v-card-text>
              <v-autocomplete
                  v-if="page.name === 'scripts'"
                  density="default"
                  class="reg-normal text-primary mt-3"
                  base-color="border-tertiary"
                  bg-color="secondary"
                  color="primary"
                  v-model="selected_script_template_id"
                  :items="script_templates"
                  @update:model-value="getScriptTemplateInfo"
                  :return-object="false"
                  item-value="value"
                  item-title="text"
                  hide-details
                  clearable>
                <template #label>
                  <span class="sm-normal text-subtle">{{ $store.getters.translate('select_script_template') }}</span>
                </template>
              </v-autocomplete>
              <template v-for="field in usual_fields">
                <!--TYPE OF WORK FIELD. EVERY LEVEL HAS ITS OWN TYPES OF WORK-->
                <v-autocomplete
                    v-if="field.name === 'type_of_work'"
                    density="default"
                    class="reg-normal text-primary mt-3"
                    base-color="border-tertiary"
                    bg-color="secondary"
                    color="primary"
                    v-model="record.type_of_work"
                    :items="field.available_options.filter(item => item.filter === record.level)"
                    :return-object="false"
                    item-value="value"
                    item-title="text"
                    hide-details
                    clearable>
                  <template #label>
                    <span class="sm-normal text-subtle">{{ $store.getters.translate('type_of_work') }}</span>
                  </template>
                </v-autocomplete>
                <!--HIDE CLOSED PROJECTS-->
                <v-autocomplete
                    v-else-if="field.name === 'project_id'"
                    density="default"
                    class="reg-normal text-primary mt-3"
                    base-color="border-tertiary"
                    bg-color="secondary"
                    color="primary"
                    v-model="record.project_id"
                    :items="field.available_options.filter(item => !item.filter)"
                    :return-object="false"
                    item-value="value"
                    item-title="text"
                    hide-details
                    clearable>
                  <template #label>
                    <span class="sm-normal text-subtle">{{ $store.getters.translate('project_id') }}</span>
                  </template>
                </v-autocomplete>
                <!--USUAL FIELDS-->
                <base-field
                    v-else
                    :key="field.name"
                    :modelValue="record[field.name]"
                    :field="field"
                    @change="usualFieldDataChanged"/>
              </template>
              <!--A LOCATION CAN HAVE MULTIPLE COMPANIES, THE NEW PROJECT HAS TO BE ATTACHED TO ONE OF THEM-->
              <v-autocomplete
                  v-if="page.model === 'project' && location_companies.length > 1"
                  density="default"
                  class="reg-normal text-primary mt-3"
                  base-color="border-tertiary"
                  bg-color="secondary"
                  color="primary"
                  v-model="record.company_id"
                  :items="location_companies"
                  :return-object="false"
                  item-value="id"
                  item-title="name"
                  hide-details
                  clearable>
                <template #label>
                  <span class="sm-normal text-subtle">{{ $store.getters.translate('company') }}<strong>*</strong></span>
                </template>
              </v-autocomplete>
              <!--CUSTOM FIELDS-->
              <base-field
                  v-if="record.custom_fields"
                  v-for="field in custom_fields"
                  :key="field.name"
                  :modelValue="record.custom_fields[field.name]"
                  :field="field"
                  @change="customFieldDataChanged"/>
              <!--UPLOAD FILES-->
              <v-row v-if="files_required" class="mt-3">
                <v-col v-cloak @drop.prevent="addDropFile" @dragover.prevent>
                  <v-file-input
                      v-model="new_files"
                      :multiple="multiple_files"
                      :label="$store.getters.translate('files')"
                      placeholder="Drop file to upload"
                      color="primary"
                      bg-color="white"
                      density="compact"
                      hide-details
                      show-size/>
                </v-col>
              </v-row>
              <v-row v-if="addresses_required" class="mt-0">
                <v-col>
                  <base-addresses v-model="record.addresses" @update="addressesChanged"/>
                </v-col>
              </v-row>
              <!--TAGS-->
              <base-tags v-if="tags_required" :isDisabled="tags_disabled" :available_tags="available_tags" :modelValue="record.tags" :modal_name="page.name" @change="tagsChanged"/>
            </v-card-text>
        </template>
        <template #footer>
          <v-card-text>
            <v-row>
              <v-col>
                <base-action-button :width="'100%'" @action="reset">
                  <template #prepend><PhX style="vertical-align: middle; margin-right: 5px;" size="20" /></template>
                  {{ $store.getters.translate("cancel") }}
                </base-action-button>
              </v-col>
              <v-col>
                <base-action-button :background="true" :width="'100%'" @action="save(true)">
                  <template #prepend><PhFloppyDisk style="vertical-align: middle; margin-right: 5px;" size="20" /></template>
                  {{ $store.getters.translate("save") }}
                </base-action-button>
              </v-col>
            </v-row>
          </v-card-text>
        </template>
      </base-modal>
    </v-card>
  </base-draggable-modal>
</template>

<script>
import {useAbility} from "@casl/vue";
import helpFunctions from "../../plugins/helpFunctions";
import BaseModal from "../../components/commonComponents/BaseModal";
import BaseDraggableModal from "../../components/commonComponents/BaseDraggableModal";
import BaseTags from "./BaseTags";
import BaseAddresses from "./BaseAddresses";
import BaseActionButton from "./BaseActionButton";
import {PhFloppyDisk, PhX} from "@phosphor-icons/vue";

export default {
  props: ["modal_name", "page", "label", "addresses_required", "files_required", "multiple_files", "available_tags", "tags_disabled"],
  components: {
    BaseModal,
    BaseDraggableModal,
    BaseTags,
    BaseAddresses,
    BaseActionButton,
    PhFloppyDisk, PhX,
  },
  setup() {
    const { can } = new useAbility();
    return { can }
  },
  data() {
    return {
      record: {},
      usual_fields: [],
      custom_fields: [],
      tags_required: false,
      //SCRIPT VARIABLES. NOT USED FOR OTHER MODELS
      selected_script_template_id: null,
      script_templates: [],
      //FILES VARIABLE
      new_files: [],
      location_companies: [],
    };
  },
  methods: {
    ...helpFunctions.modal_functions,
    async load() {
      this.cleanRecord();
      this.openModal(this.modal_name);
      await this.getFields();
      if(this.page.model === 'script') {
        await this.getScriptTemplates();
      }
    },
    async save(reload) {
      if (helpFunctions.modelFieldsFilled(this.$toast, this.record, this.usual_fields)) {
        if(this.page.model === 'translation') {
          let new_key = this.$lodash.lowerCase(this.record.key);

          this.record.key = new_key.replaceAll('(', "_").replaceAll(')', "_").replaceAll('[', "_").replaceAll(']', "_")
              .replaceAll('-', "_").replaceAll('/', "_").replaceAll('\\', "_").replace(/ /g, "_");
        }
        let new_record = await helpFunctions.createModel(this.$toast, this.page.name, this.record);
        if(new_record) {
          if(this.multiple_files && this.new_files.length > 0) {
            await this.uploadFiles(this.new_files, new_record.id);
          }
          else if(typeof this.new_files === "object" && this.new_files.name) {
            await this.uploadOneFile(this.new_files, new_record.id);
          }
          if(this.page.model === 'translation') {
            await this.$store.dispatch("refreshTranslations");
          }
          this.$emit('refresh', new_record);
          if(reload) {
            this.reset();
          }
          else {
            this.record.id = null;
            this.record.name = "";
            this.record.description = "";
          }
        }
      }
    },
    reset() {
      this.cleanRecord();
      this.new_files = [];
      this.selected_script_template_id = null;
      this.closeModal(this.modal_name);
    },
    cleanRecord() {
      if(this.page.model === 'company') {
        this.record = {
          custom_fields: {},
        };
      }
      else if(this.page.model === 'scripttemplate' || this.page.model === 'script') {
        this.record = {
          parameters: {
            groups: [],
            questions: [],
            answers: [],
          },
        };
      }
      else {
        this.record = {};
      }
    },
    usualFieldDataChanged(data) {
      if(this.record[data[0]] !== data[1]) {
        this.record[data[0]] = data[1];
      }
    },
    customFieldDataChanged(data) {
      this.record.custom_fields[data[0]] = data[1];
    },
    addressesChanged(updated_addresses) {
      this.record.addresses = updated_addresses;
    },
    tagsChanged(data) {
      this.record.tags = data;
    },
    addDropFile(e) {
      for (let i = 0; i < e.dataTransfer.files.length; i++) {
        this.new_files.push(e.dataTransfer.files[i]);
      }
    },
    async uploadFiles(files, record_id) {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append("attachment[]", files[i]);
      }
      formData.append("id", record_id);
      await helpFunctions.saveFile(this.$toast, this.page.name, formData);
    },
    async uploadOneFile(file, record_id) {
      const formData = new FormData();
      formData.append("attachment", file);
      formData.append("id", record_id);
      await helpFunctions.saveFile(this.$toast, this.page.name, formData);
    },
    async getFields() {
      const response = await helpFunctions.getFields(this.page.model, "show_on_creation");
      this.tags_required = response.tags_required;
      this.usual_fields = response.usual_fields;
      this.custom_fields = response.custom_fields;
    },
    //SCRIPT CUSTOM FUNCTION
    async getScriptTemplates() {
      this.script_templates = await helpFunctions.getAdditionalModelsForDropdowns("scripttemplate", "id", ["name"]);
    },
    async getScriptTemplateInfo() {
      if(this.selected_script_template_id) {
        let response = await helpFunctions.getOneFullAdditionalModel(this.$toast, "scripttemplate", this.selected_script_template_id, true);
        this.record.name = response.name;
        this.record.description = response.description;
        this.record.type = response.type;
        this.record.language = response.language;
        this.record.parameters = response.parameters;
      }
      else {
        this.record.name = null;
        this.record.description = null;
        this.record.type = null;
        this.record.language = null;
        this.record.parameters = {};
      }
    },
  },
  watch: {
    'record.location_id': {
      async handler() {
        this.location_companies = [];
        if(this.page.model === 'project' && this.record.location_id) {
          let location = await helpFunctions.loadModel(this.$toast, "locations", this.record.location_id);
          this.location_companies = location ? location.companies : [];
          this.record.company_id = this.location_companies.length > 0 ? this.location_companies[0].id : null;
        }
      },
    },
  },
};
</script>